'use client';

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { cn } from '@/shared/lib/cn';

// ===== Colors (matching Android/Apple palette) =====
const COLORS = {
  tealLight: [0x51, 0xf6, 0xee],
  teal: [0x0a, 0xc2, 0xb9],
  tealBright: [0x0d, 0xf2, 0xe7],
  cyanLight: [0x69, 0xf7, 0xf0],
  mint: [0x34, 0xd3, 0x99],
  blueBright: [0x60, 0xa5, 0xfa],
  indigo: [0x63, 0x66, 0xf1],
  purpleLight: [0xc0, 0x84, 0xfc],
} as const;

// ===== Animation config =====
const PHASE_DURATION = 120; // seconds
const OPACITY_PULSE_DURATION = 3.5;
const OPACITY_MIN = 0.88;
const OPACITY_MAX = 1.0;

const FRAME_FRACTION = 0.15;
const BASE_WIDTH_FRACTION = 0.14;
const STRAND_ALPHA = 0.92;
const TRAVEL_SPEED = 1.0;
const SEGMENTS = 7;
const BLUR_FRACTION = 0.24;
const CORNER_RADIUS_FRACTION = 0.7;

const TWO_PI = Math.PI * 2;

// ===== Strand configs (identical to Android/Apple) =====
interface StrandConfig {
  color: readonly [number, number, number];
  pointCount: number;
  arcLength: number;
  startOffset: number;
  speedMultiplier: number;
  widthMultiplier: number;
  waveFrequency: number;
  phaseOffset: number;
  amplitudeMultiplier: number;
}

const STRAND_CONFIGS: StrandConfig[] = [
  { color: COLORS.tealLight, pointCount: 72, arcLength: 0.75, startOffset: 0.0, speedMultiplier: 2, widthMultiplier: 1.0, waveFrequency: 3.0, phaseOffset: 0.0, amplitudeMultiplier: 1.0 },
  { color: COLORS.blueBright, pointCount: 64, arcLength: 0.62, startOffset: 0.13, speedMultiplier: 2, widthMultiplier: 1.5, waveFrequency: 3.5, phaseOffset: 0.79, amplitudeMultiplier: 0.9 },
  { color: COLORS.purpleLight, pointCount: 76, arcLength: 0.8, startOffset: 0.25, speedMultiplier: 3, widthMultiplier: 0.7, waveFrequency: 2.7, phaseOffset: 1.57, amplitudeMultiplier: 1.15 },
  { color: COLORS.teal, pointCount: 60, arcLength: 0.58, startOffset: 0.4, speedMultiplier: 2, widthMultiplier: 1.3, waveFrequency: 3.2, phaseOffset: 2.36, amplitudeMultiplier: 0.85 },
  { color: COLORS.cyanLight, pointCount: 68, arcLength: 0.72, startOffset: 0.52, speedMultiplier: 3, widthMultiplier: 0.85, waveFrequency: 3.8, phaseOffset: 3.14, amplitudeMultiplier: 1.1 },
  { color: COLORS.indigo, pointCount: 56, arcLength: 0.55, startOffset: 0.65, speedMultiplier: 2, widthMultiplier: 1.6, waveFrequency: 2.5, phaseOffset: 3.93, amplitudeMultiplier: 0.92 },
  { color: COLORS.tealBright, pointCount: 80, arcLength: 0.82, startOffset: 0.78, speedMultiplier: 3, widthMultiplier: 0.8, waveFrequency: 3.3, phaseOffset: 4.71, amplitudeMultiplier: 1.08 },
  { color: COLORS.mint, pointCount: 66, arcLength: 0.66, startOffset: 0.9, speedMultiplier: 2, widthMultiplier: 1.35, waveFrequency: 3.0, phaseOffset: 5.5, amplitudeMultiplier: 0.95 },
];

// ===== Geometry helpers =====

function perimeterPoint(
  t: number,
  width: number,
  height: number,
  inset: number,
  cornerRadius: number
): [number, number] {
  const r = Math.min(cornerRadius, Math.min(width, height) / 2);
  const straightH = Math.max(width - 2 * r, 0);
  const straightV = Math.max(height - 2 * r, 0);
  const arcLen = (r * Math.PI) / 2;
  const totalLen = 2 * straightH + 2 * straightV + 4 * arcLen;
  if (totalLen < 0.001) return [width / 2, height / 2];

  let pos = (((t % 1) + 1) % 1) * totalLen;
  const er = Math.max(r - inset, 0.01);

  // Top edge (L→R)
  if (pos <= straightH) return [r + pos, inset];
  pos -= straightH;

  // Top-right arc
  if (pos <= arcLen) {
    const a = -Math.PI / 2 + (arcLen > 0 ? pos / arcLen : 0) * (Math.PI / 2);
    return [(width - r) + er * Math.cos(a), r + er * Math.sin(a)];
  }
  pos -= arcLen;

  // Right edge (T→B)
  if (pos <= straightV) return [width - inset, r + pos];
  pos -= straightV;

  // Bottom-right arc
  if (pos <= arcLen) {
    const a = (arcLen > 0 ? pos / arcLen : 0) * (Math.PI / 2);
    return [(width - r) + er * Math.cos(a), (height - r) + er * Math.sin(a)];
  }
  pos -= arcLen;

  // Bottom edge (R→L)
  if (pos <= straightH) return [width - r - pos, height - inset];
  pos -= straightH;

  // Bottom-left arc
  if (pos <= arcLen) {
    const a = Math.PI / 2 + (arcLen > 0 ? pos / arcLen : 0) * (Math.PI / 2);
    return [r + er * Math.cos(a), (height - r) + er * Math.sin(a)];
  }
  pos -= arcLen;

  // Left edge (B→T)
  if (pos <= straightV) return [inset, height - r - pos];
  pos -= straightV;

  // Top-left arc
  const frac = arcLen > 0 ? Math.min(Math.max(pos / arcLen, 0), 1) : 0;
  const a = Math.PI + frac * (Math.PI / 2);
  return [r + er * Math.cos(a), r + er * Math.sin(a)];
}

function drawSmoothPath(
  ctx: CanvasRenderingContext2D,
  points: [number, number][]
) {
  const n = points.length;
  if (n < 2) return;
  const first = points[0]!;
  if (n === 2) {
    const second = points[1]!;
    ctx.moveTo(first[0], first[1]);
    ctx.lineTo(second[0], second[1]);
    return;
  }

  ctx.moveTo(first[0], first[1]);
  const tension = 1 / 3;
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)]!;
    const p1 = points[i]!;
    const p2 = points[Math.min(i + 1, n - 1)]!;
    const p3 = points[Math.min(i + 2, n - 1)]!;
    ctx.bezierCurveTo(
      p1[0] + (p2[0] - p0[0]) * tension,
      p1[1] + (p2[1] - p0[1]) * tension,
      p2[0] - (p3[0] - p1[0]) * tension,
      p2[1] - (p3[1] - p1[1]) * tension,
      p2[0],
      p2[1]
    );
  }
}

function rgba(
  color: readonly [number, number, number],
  alpha: number
): string {
  return `rgba(${color[0]},${color[1]},${color[2]},${alpha})`;
}

// ===== Drawing =====

function drawStrands(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  elapsed: number
) {
  const frameThickness = Math.min(width * FRAME_FRACTION, height / 2);
  const amplitude = frameThickness * 0.34;
  const baseStrokeWidth = frameThickness * BASE_WIDTH_FRACTION;
  const blurRadius = Math.max(frameThickness * BLUR_FRACTION, 1);
  const cRadius = frameThickness * CORNER_RADIUS_FRACTION;

  const phase =
    ((elapsed % PHASE_DURATION) / PHASE_DURATION) * TWO_PI;

  const pulseT =
    ((elapsed % (OPACITY_PULSE_DURATION * 2)) / (OPACITY_PULSE_DURATION * 2));
  const rawPulse = Math.sin(pulseT * Math.PI);
  const opacityPulse =
    OPACITY_MIN + (OPACITY_MAX - OPACITY_MIN) * rawPulse;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const cfg of STRAND_CONFIGS) {
    const effectivePhase = phase * cfg.speedMultiplier;
    const arcStart =
      (cfg.startOffset +
        (phase / TWO_PI) * TRAVEL_SPEED * cfg.speedMultiplier) %
      1;

    const points: [number, number][] = [];
    const insets: number[] = [];

    for (let i = 0; i < cfg.pointCount; i++) {
      const localT = i / (cfg.pointCount - 1);
      const globalT = (arcStart + localT * cfg.arcLength) % 1;
      const angle =
        localT * cfg.waveFrequency * TWO_PI +
        cfg.phaseOffset +
        effectivePhase;
      const oscillation =
        Math.sin(angle) * amplitude * cfg.amplitudeMultiplier;
      const inset = oscillation;
      points.push(
        perimeterPoint(globalT, width, height, inset, cRadius)
      );
      insets.push(inset);
    }

    const strandWidth = baseStrokeWidth * cfg.widthMultiplier;

    // Glow pass: wide soft halo
    ctx.save();
    ctx.filter = `blur(${blurRadius}px)`;
    ctx.strokeStyle = rgba(cfg.color, 0.28 * opacityPulse);
    ctx.lineWidth = strandWidth * 3.5;
    ctx.beginPath();
    drawSmoothPath(ctx, points);
    ctx.stroke();
    ctx.restore();

    // Main pass: segmented for variable thickness + edge fade
    for (let seg = 0; seg < SEGMENTS; seg++) {
      const rawStart = (seg * cfg.pointCount) / SEGMENTS | 0;
      const rawEnd = ((seg + 1) * cfg.pointCount) / SEGMENTS | 0;
      const startIdx = Math.max(0, rawStart - 1);
      const endIdx = Math.min(cfg.pointCount, rawEnd + 1);
      if (endIdx - startIdx < 3) continue;

      const segPoints = points.slice(startIdx, endIdx);

      // Steep bell-curve taper (sin²)
      const segNorm = (seg + 0.5) / SEGMENTS;
      const taper = Math.sin(segNorm * Math.PI);
      const taperSteep = taper * taper;
      const segWidth = strandWidth * (0.05 + 0.95 * taperSteep);

      // Edge proximity fade
      let insetSum = 0;
      for (let idx = startIdx; idx < endIdx; idx++) insetSum += insets[idx]!;
      const avgInset = insetSum / (endIdx - startIdx);
      const edgeFade = Math.max(
        0.15,
        Math.min(1, 1 - avgInset / (frameThickness * 1.2))
      );

      const segAlpha =
        STRAND_ALPHA *
        opacityPulse *
        Math.max(taperSteep, 0.05) *
        edgeFade;

      ctx.strokeStyle = rgba(cfg.color, segAlpha);
      ctx.lineWidth = segWidth;
      ctx.beginPath();
      drawSmoothPath(ctx, segPoints);
      ctx.stroke();
    }
  }
}

// ===== React Component =====

interface AIGlowBorderProps {
  isActive: boolean;
  className?: string;
  children: React.ReactNode;
}

export function AIGlowBorder({
  isActive,
  className,
  children,
}: AIGlowBorderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const opacityRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const [visible, setVisible] = useState(false);

  // Cache prefers-reduced-motion via listener instead of polling every frame
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mql.matches;
    const handler = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fade in/out
    const target = isActive ? 1 : 0;
    const speed = 0.02; // ~0.8s at 60fps
    if (Math.abs(opacityRef.current - target) > 0.001) {
      opacityRef.current += (target - opacityRef.current) * speed * 3;
      if (Math.abs(opacityRef.current - target) < 0.01) {
        opacityRef.current = target;
      }
    }

    if (opacityRef.current <= 0.001) {
      opacityRef.current = 0;
      setVisible(false);
      return;
    }

    const rect = canvas.parentElement?.getBoundingClientRect();
    if (!rect) return;

    const dpr = window.devicePixelRatio || 1;
    const w = rect.width;
    const h = rect.height;

    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.globalAlpha = opacityRef.current;

    const elapsed = reducedMotionRef.current ? 0 : performance.now() / 1000;

    drawStrands(ctx, w, h, elapsed);
    ctx.restore();

    rafRef.current = requestAnimationFrame(animate);
  }, [isActive]);

  // Start animation when becoming active
  useEffect(() => {
    if (isActive) {
      setVisible(true);
    }
  }, [isActive]);

  // Run rAF loop while canvas is mounted
  useEffect(() => {
    if (!visible) return;
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible, animate]);

  return (
    <div className={cn('relative', className)}>
      {children}
      {visible && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-none z-40"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
